/**
 * Prompt blocklist — client-side composer gate.
 *
 * Behaviourally identical to VIVIDEO/PROMPT_BLOCKLIST.py and to the Flutter
 * port in vivideo-mobile/lib/core/moderation/prompt_blocklist.dart. All three
 * read the same generated data (blocklistData.ts) and apply the same checks in
 * the same order. `python3 VIVIDEO/verify_blocklist_parity.py` diffs them over
 * a shared corpus — run it after any change here.
 *
 * NOTE: this runs in the browser, so it is a UX gate, not a security boundary.
 * A determined caller can POST straight to /v2-create-order. The same module
 * belongs server-side in create-order / v2-create-order for the real gate.
 */
import {
  FOLD, LEET, HARD_WORDS, MINOR_CONTEXT, MINOR_SUGGESTIVE, DEOBF_WORDS,
  PROFANITY, I18N_WORDS, I18N_PHRASES, I18N_SUBSTR, MINOR_SUBSTR,
  RE_MULTI_SRC, RE_MINOR_AGE_SRC, RE_GUARD_SRCS,
} from './blocklistData';

export type BlockReason =
  | 'explicit_term'
  | 'explicit_phrase'
  | 'explicit_term_i18n'
  | 'minor_sexual'
  | 'obfuscated_term'
  | 'profanity';

export interface BlocklistResult {
  blocked: boolean;
  reason: BlockReason | null;
  /** The matched term. For logging/analytics only — never render it back to
   *  the user; echoing their slur into the UI is its own problem. */
  term: string | null;
}

const PASS: BlocklistResult = { blocked: false, reason: null, term: null };

/** Owner decision: profanity alone is not what triggered the store removal. */
const ENABLE_PROFANITY = false;

// Compiled once at module scope — never per keystroke.
const RE_MULTI = new RegExp(RE_MULTI_SRC, 'i');
const RE_MINOR_AGE = new RegExp(RE_MINOR_AGE_SRC, 'i');
const RE_GUARDS = RE_GUARD_SRCS.map((s) => new RegExp(s, 'i'));
const RE_TOKEN = /\p{L}+/gu;
// Letter-class boundaries rather than \b — JS's \b is ASCII-only even under /u, so
// it would disagree with the Python and Dart ports on any non-Latin script.
// NO LOOKBEHIND here: Safari before 16.4 cannot compile one, and this module sits in
// the app's entry chunk, so the old negative lookbehind threw while the app was
// starting and left those browsers on a blank page. The leading non-letter is captured instead and
// put back by normalize(); the result is identical to the lookbehind form.
const RE_SPACED_OUT = /(^|\P{L})((?:\p{L}[\s.\-_*]+){2,}\p{L})(?!\p{L})/gu;
const RE_NO_SPACE_SCRIPT = /[一-鿿぀-ヿ฀-๿가-힯]/u;

/**
 * Every phrase's own tokens, precomputed once. A raw substring test matches
 * across word boundaries — French "a poil" (naked) is inside "a poilu" (hairy),
 * so "a poilu terrier" was blocked. Requiring each of the phrase's tokens to be
 * present as a WHOLE token fixes that, and doubles as a cheap pre-filter:
 * clean prompts now skip nearly every substring scan.
 */
const PHRASE_TOKENS: ReadonlyArray<readonly [string, readonly string[]]> =
  I18N_PHRASES.map((p) => [p, Array.from(p.matchAll(/\p{L}+/gu), (m) => m[0])] as const);

function foldText(s: string): string {
  let out = '';
  for (const ch of s) out += FOLD[ch] ?? ch;
  return out;
}

function applyLeet(s: string): string {
  let out = '';
  for (const ch of s) out += LEET[ch] ?? ch;
  return out;
}

function tokensOf(s: string): Set<string> {
  const set = new Set<string>();
  for (const m of s.matchAll(RE_TOKEN)) set.add(m[0]);
  return set;
}

function intersect(tokens: Set<string>, words: ReadonlySet<string>): string | null {
  const hits: string[] = [];
  for (const t of tokens) if (words.has(t)) hits.push(t);
  if (!hits.length) return null;
  hits.sort();
  return hits[0];
}

/**
 * `plain` keeps digits: the age regex must run on it, because leet folding
 * turns "14 year old" into "ia year old" and every age would vanish.
 */
function normalize(text: string): { plain: string; normal: string; deobf: string } {
  const plain = foldText(text ?? '').toLowerCase();
  const normal = applyLeet(plain);
  const deobf = normal.replace(RE_SPACED_OUT, (_m, pre: string, run: string) => pre + run.replace(/[\s.\-_*]/g, ''));
  return { plain, normal, deobf };
}

export function checkPrompt(prompt: string): BlocklistResult {
  if (!prompt || !prompt.trim()) return PASS;

  const { plain, normal, deobf } = normalize(prompt);
  const tokens = tokensOf(normal);

  const hard = intersect(tokens, HARD_WORDS);
  if (hard) return { blocked: true, reason: 'explicit_term', term: hard };

  if (ENABLE_PROFANITY) {
    const p = intersect(tokens, PROFANITY);
    if (p) return { blocked: true, reason: 'profanity', term: p };
  }

  for (const rx of RE_GUARDS) {
    const m = rx.exec(normal);
    if (m) return { blocked: true, reason: 'explicit_term', term: m[0] };
  }

  const multi = RE_MULTI.exec(normal);
  if (multi) return { blocked: true, reason: 'explicit_phrase', term: multi[0] };

  const i18nWord = intersect(tokens, I18N_WORDS);
  if (i18nWord) return { blocked: true, reason: 'explicit_term_i18n', term: i18nWord };
  for (const [ph, phToks] of PHRASE_TOKENS) {
    if (phToks.every((t) => tokens.has(t)) && normal.includes(ph)) {
      return { blocked: true, reason: 'explicit_term_i18n', term: ph };
    }
  }
  const hasNoSpaceScript = RE_NO_SPACE_SCRIPT.test(normal);
  if (hasNoSpaceScript) {
    for (const sub of I18N_SUBSTR) {
      if (normal.includes(sub)) return { blocked: true, reason: 'explicit_term_i18n', term: sub };
    }
  }

  const isMinor =
    intersect(tokens, MINOR_CONTEXT) !== null ||
    RE_MINOR_AGE.test(plain) ||
    (hasNoSpaceScript && MINOR_SUBSTR.some((m) => normal.includes(m)));
  if (isMinor) {
    const soft = intersect(tokens, MINOR_SUGGESTIVE);
    if (soft) return { blocked: true, reason: 'minor_sexual', term: soft };
  }

  // Only tokens CREATED by de-obfuscation count — otherwise a word that
  // legitimately survived its guard ("the naked eye") gets re-flagged here.
  if (deobf !== normal) {
    const fresh = new Set<string>();
    for (const t of tokensOf(deobf)) if (!tokens.has(t)) fresh.add(t);
    const hit = intersect(fresh, DEOBF_WORDS);
    if (hit) return { blocked: true, reason: 'obfuscated_term', term: hit };
  }

  return PASS;
}

/** i18n key for the warning shown in the composer. Minors and non-consent get
 *  their own copy because the generic "adjust your prompt" line reads as an
 *  invitation to retry, which is the wrong message for that category. */
export function blockMessageKey(reason: BlockReason | null): string {
  return reason === 'minor_sexual'
    ? 'moderation.blockedMinor'
    : 'moderation.blocked';
}
